import React, { useState, useEffect } from 'react';
import { Truck, Plus, Copy, CheckCircle, Clock, Search, Trash2, Power, Mail, AlertCircle, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/admin/Sidebar';
import Header from '../../components/admin/Header';
import api from '../../utils/api';
import { formatDate } from '../../utils/helpers';
import { useAuth } from '../../context/AuthContext';
import './SuppliersPage.css';
import './DirectoryModals.css';

export default function SuppliersPage() {
  const { isAdmin } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', emails: '' });
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [activationLinks, setActivationLinks] = useState({});
  const [search, setSearch] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deactivateConfirm, setDeactivateConfirm] = useState(null);
  const [deactivating, setDeactivating] = useState(false);

  const fetchSuppliers = async () => {
    try {
      const res = await api.get('/suppliers');
      setSuppliers(res.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchSuppliers(); }, []);

  useEffect(() => {
    if (showModal) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showModal]);

  useEffect(() => {
    const handleRefresh = () => {
      fetchSuppliers();
      toast.success('Data refreshed');
    };

    window.addEventListener('navigationRefresh', handleRefresh);
    return () => window.removeEventListener('navigationRefresh', handleRefresh);
  }, []);

  const flattenedSuppliers = suppliers.flatMap((supplier) => {
    const allEmails = [supplier.email, ...(supplier.emails || [])].filter(Boolean);
    const uniqueEmails = [...new Set(allEmails.map((email) => String(email).trim().toLowerCase()).filter(Boolean))];
    const activatedEmails = Array.isArray(supplier.activatedEmails)
      ? supplier.activatedEmails.map((email) => String(email).trim().toLowerCase())
      : [];

    return uniqueEmails.map((email) => {
      const emailAccountsActivated = Array.isArray(supplier.emailAccounts)
        ? supplier.emailAccounts.some(acc => String(acc.email).trim().toLowerCase() === email && acc.isActivated)
        : false;

      const isActivated = activatedEmails.includes(email)
        || emailAccountsActivated
        || (supplier.isActivated && email === String(supplier.email || '').trim().toLowerCase());
      return {
        ...supplier,
        email,
        isPrimary: email === String(supplier.email || '').trim().toLowerCase(),
        isActivated,
        activationStatus: isActivated
          ? 'activated'
          : supplier.isActive === false
            ? 'deactivated'
            : 'pending'
      };
    });
  });

  const supplierOptions = [...new Map(suppliers.map((supplier) => [supplier._id, supplier]))].map((supplier) => supplier).sort((a, b) => {
    const aName = String(a?.name || '').trim().toLowerCase();
    const bName = String(b?.name || '').trim().toLowerCase();
    return aName.localeCompare(bName);
  });

  const filtered = flattenedSuppliers.filter((row) => {
    const matchesSearch = row.name.toLowerCase().includes(search.toLowerCase()) || row.email.toLowerCase().includes(search.toLowerCase());
    const matchesSupplierFilter = supplierFilter ? row._id === supplierFilter : true;
    return matchesSearch && matchesSupplierFilter;
  });
  const supplierMetrics = [
    { label: 'Supplier companies', value: supplierOptions.length, icon: Truck, tone: 'red' },
    { label: 'Contact accounts', value: flattenedSuppliers.length, icon: Mail, tone: 'blue' },
    { label: 'Activated accounts', value: flattenedSuppliers.filter(row => row.isActivated).length, icon: CheckCircle, tone: 'green' },
    { label: 'Awaiting activation', value: flattenedSuppliers.filter(row => row.activationStatus === 'pending').length, icon: Clock, tone: 'amber' }
  ];

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await api.post('/suppliers', form);
      const recipientLabel = (res.data.supplier?.emails?.length ? res.data.supplier.emails.join(', ') : form.email);
      toast.success(`Supplier invitations sent to ${recipientLabel}!`);
      setActivationLinks(prev => ({ ...prev, [res.data.supplier.id]: res.data.activationLinks || [{ email: form.email, link: res.data.activationLink }] }));
      setForm({ name: '', email: '', emails: '' });
      setShowModal(false);
      fetchSuppliers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create supplier');
    } finally { setCreating(false); }
  };

  const openEditModal = (supplier) => {
    setEditingSupplier(supplier);
    setForm({ name: supplier.name, email: supplier.email || '', emails: (supplier.emails || []).join(', ') });
    setShowModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingSupplier) return;
    setUpdating(true);
    try {
      await api.patch(`/suppliers/${editingSupplier._id}`, form);
      toast.success('Supplier updated successfully!');
      setForm({ name: '', email: '', emails: '' });
      setEditingSupplier(null);
      setShowModal(false);
      fetchSuppliers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update supplier');
    } finally { setUpdating(false); }
  };

  const regenerateLink = async (supplierId, email = '') => {
    try {
      const res = await api.post(`/suppliers/${supplierId}/regenerate-link`, email ? { email } : {});
      setActivationLinks(prev => ({ ...prev, [supplierId]: res.data.activationLinks || [{ email: '', link: res.data.activationLink }] }));
      toast.success('Regenerated and resent activation email!');
    } catch (err) {
      toast.error('Failed to regenerate link');
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Activation link copied!');
  };

  const handleDeleteSupplier = async (supplierId) => {
    setDeleting(true);
    try {
      await api.delete(`/suppliers/${supplierId}`);
      toast.success('Supplier deleted successfully!');
      fetchSuppliers();
      setDeleteConfirm(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete supplier');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleSupplierActive = async (supplierId) => {
    setDeactivating(true);
    try {
      const res = await api.patch(`/suppliers/${supplierId}/toggle-active`);
      toast.success(res.data?.message || 'Supplier access updated!');
      fetchSuppliers();
      setDeactivateConfirm(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update supplier access');
    } finally {
      setDeactivating(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="page-layout suppliers-page">
      <Sidebar />
      <div className="main-content">
        <Header title="Suppliers" subtitle="Manage supplier accounts and automated activation invites" />
        <div className="page-body suppliers-page-body">

          <div className="suppliers-summary-grid" aria-label="Supplier account metrics">
            {supplierMetrics.map(({ label, value, icon: Icon, tone }) => (
              <div className="suppliers-summary-item" key={label}>
                <span className={`suppliers-summary-icon tone-${tone}`}><Icon size={18} /></span>
                <span className="suppliers-summary-copy">
                  <span className="suppliers-summary-label">{label}</span>
                  <span className="suppliers-summary-value">{value}</span>
                </span>
              </div>
            ))}
          </div>

          {/* Toolbar */}
          <div className="card suppliers-toolbar">
            <div className="suppliers-search">
              <Search size={16} className="suppliers-search-icon" />
              <input className="form-input suppliers-search-input"
                placeholder="Search suppliers…"
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div className="suppliers-filter">
              <label htmlFor="supplier-filter">Filter by supplier</label>
              <select id="supplier-filter" className="form-input" value={supplierFilter} onChange={e => setSupplierFilter(e.target.value)}>
                <option value="">All Suppliers</option>
                {supplierOptions.map((supplier) => (
                  <option key={supplier._id} value={supplier._id}>{supplier.name}</option>
                ))}
              </select>
            </div>
            <button className="btn btn-primary suppliers-add-button" onClick={() => {
              setEditingSupplier(null);
              setForm({ name: '', email: '', emails: '' });
              setShowModal(true);
            }}>
              <Plus size={16} /> Add Supplier
            </button>
          </div>

          {/* Pending Activation Links */}
          {Object.entries(activationLinks).length > 0 && (
            <div className="card suppliers-activation-panel">
              <p className="suppliers-activation-title">
                 <Mail size={16} /> Activation Emails Sent!
              </p>
              <p className="suppliers-activation-description">
                An automated email has been sent. You can also manually copy the links below if needed.
              </p>
              {Object.entries(activationLinks).map(([id, links]) => {
                const supplier = suppliers.find(s => s._id === id);
                const items = Array.isArray(links) ? links : [{ email: '', link: links }];
                return (
                  <div key={id} className="suppliers-activation-group">
                    <div className="suppliers-activation-company">{supplier?.name}</div>
                    {items.map((item, index) => (
                      <div key={`${id}-${index}`} className="suppliers-activation-link-row">
                        {item.email && (
                          <span className="suppliers-activation-email">
                            {item.email}
                          </span>
                        )}
                        <code className="suppliers-activation-code">{item.link}</code>
                        <button className="btn btn-secondary btn-sm" onClick={() => copyLink(item.link)}>
                          <Copy size={13} /> Copy
                        </button>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          {/* Table */}
          <div className="card suppliers-table-shell">
            {loading ? (
              <div className="suppliers-loading">
                <div className="spinner spinner-dark" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="empty-state suppliers-empty">
                <Truck size={40} />
                <h3>{flattenedSuppliers.length === 0 ? 'No suppliers yet' : 'No contacts match these filters'}</h3>
                <p>{flattenedSuppliers.length === 0 ? "Add a supplier to get started. They'll receive an automated invite to set up their account." : 'Try another search or choose a different supplier.'}</p>
                {flattenedSuppliers.length === 0 && <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
                  <Plus size={14} /> Add First Supplier
                </button>}
              </div>
            ) : (
              <div className="table-wrap">
                <table className="suppliers-table">
                  <thead>
                    <tr>
                      <th>S/N</th>
                      <th>Supplier</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Added</th>
                      <th>Invitation</th>
                      <th>Manage</th>
                      <th>Access</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(filtered.reduce((groups, row) => {
                      if (!groups[row._id]) groups[row._id] = [];
                      groups[row._id].push(row);
                      return groups;
                    }, {})).map(([supplierId, rows], groupIndex) => {
                      return rows.map((row, index) => (
                        <tr key={`${row._id}-${row.email}`} className={`suppliers-table-row supplier-group-${groupIndex % 2 === 0 ? 'odd' : 'even'} ${index === 0 ? 'supplier-group-start' : ''}`}>
                          <td>{filtered.findIndex(filteredRow => filteredRow._id === row._id && filteredRow.email === row.email) + 1}</td>
                          <td>
                            {index === 0 ? (
                              <div className="suppliers-company-cell">
                                <div className="suppliers-avatar">
                                  {row.name?.charAt(0).toUpperCase() || '?'}
                                </div>
                                <div className="suppliers-company-copy">
                                  <span className="suppliers-company-name">{row.name}</span>
                                  <span className="suppliers-contact-count">{rows.length} contact{rows.length > 1 ? 's' : ''}</span>
                                </div>
                              </div>
                            ) : null}
                          </td>
                          <td className="suppliers-email-cell">
                            <div>
                              <span>{row.email}</span>
                              {!row.isPrimary && (
                                <span className="suppliers-additional-contact">Additional contact</span>
                              )}
                            </div>
                          </td>
                          <td>
                            {row.isActive === false ? (
                              <span className="badge badge-gray"><Power size={12} /> Deactivated</span>
                            ) : row.isActivated ? (
                              <span className="badge badge-success"><CheckCircle size={12} /> Activated</span>
                            ) : (
                              <span className="badge badge-warning"><Clock size={12} /> Pending Activation</span>
                            )}
                          </td>
                          <td className="suppliers-date">{formatDate(row.createdAt)}</td>
                          <td>
                            {!row.isActivated ? (
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => regenerateLink(row._id, row.email)}
                                title="Resend activation email"
                              >
                                <Mail size={13} /> Resend
                              </button>
                            ) : (
                              <span className="suppliers-activated-label">Activated</span>
                            )}
                          </td>
                          <td>
                            <button
                              className="btn btn-secondary btn-sm suppliers-edit-button"
                              onClick={() => openEditModal(row)}
                              title="Edit supplier"
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-danger btn-sm suppliers-delete-button"
                              onClick={() => setDeleteConfirm(row)}
                              title="Delete supplier"
                            >
                              <Trash2 size={13} /> Delete
                            </button>
                          </td>
                          <td>
                            <button 
                              className={`btn ${row.isActive === false ? 'btn-secondary' : 'btn-warning'} btn-sm suppliers-access-button`}
                              onClick={() => setDeactivateConfirm(row)}
                              title={row.isActive === false ? 'Reactivate supplier' : 'Deactivate supplier'}
                            >
                              <Power size={13} /> {row.isActive === false ? 'Reactivate' : 'Deactivate'}
                            </button>
                          </td>
                        </tr>
                      ));
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Supplier Modal */}
      {showModal && (
        <div className="modal-overlay directory-modal-overlay">
          <div className="modal directory-modal">
            <div className="modal-header directory-modal-header">
              <div className="directory-modal-heading">
                <span className="directory-modal-icon"><Truck size={20} /></span>
                <div>
                  <span className="directory-modal-eyebrow">Supplier portal</span>
                  <h3>{editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}</h3>
                  <p>{editingSupplier ? 'Update company and contact details.' : 'Create a supplier account and send portal invitations.'}</p>
                </div>
              </div>
              <button type="button" className="directory-modal-close" onClick={() => {
                setShowModal(false);
                setEditingSupplier(null);
                setForm({ name: '', email: '', emails: '' });
              }} aria-label="Close dialog"><X size={18} /></button>
            </div>
            <form onSubmit={editingSupplier ? handleUpdate : handleCreate} className="directory-modal-form">
              <div className="modal-body directory-modal-body">
                <div className="form-group directory-modal-form-group">
                  <label className="form-label">Company / Supplier Name *</label>
                  <input className="form-input" placeholder="e.g. UFLEX Nigeria Ltd"
                    value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div className="form-group directory-modal-form-group">
                  <label className="form-label">Primary Contact Email *</label>
                  <input className="form-input" type="email" placeholder="contact@supplier.com"
                    value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
                </div>
                <div className="form-group directory-modal-form-group">
                  <label className="form-label">Additional Email Addresses</label>
                  <textarea className="form-input" rows={3} placeholder="Enter additional emails separated by commas or new lines"
                    value={form.emails} onChange={e => setForm({ ...form, emails: e.target.value })} />
                  <p className="directory-modal-help">
                    Add more than one email for the same supplier company. Each person will receive their own portal activation email.
                  </p>
                </div>
                <div className="directory-modal-note">
                  <AlertCircle size={17} />
                  <div>
                    <strong>{editingSupplier ? 'Update supplier details:' : 'Automated Invitation:'}</strong> {editingSupplier ? 'The supplier record will be updated immediately.' : 'By clicking "Send Email Invitation", the supplier will receive an email with instructions to set up their portal account.'}
                  </div>
                </div>
              </div>
              <div className="modal-footer directory-modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={creating || updating}>
                  {creating || updating ? <span className="spinner" /> : editingSupplier ? 'Save Changes' : <><Mail size={15} /> Send Email Invitation</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <div>
                <h3 style={{ fontSize: '1.0625rem', fontWeight: 700, color: 'var(--danger)' }}>Delete Supplier</h3>
                <p style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', marginTop: 2 }}>
                  This action cannot be undone.
                </p>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setDeleteConfirm(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-700)', marginBottom: 16, lineHeight: 1.6 }}>
                Are you sure you want to delete <strong>{deleteConfirm.name}</strong> from the system? 
              </p>
              <div style={{
                background: 'var(--danger-light)', borderRadius: 'var(--radius-md)',
                padding: '12px 14px', fontSize: '0.8125rem', color: 'var(--danger)', lineHeight: 1.5
              }}>
                <strong>⚠️ Warning:</strong> Deleting this supplier will remove all associated records and cannot be recovered.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)} disabled={deleting}>Cancel</button>
              <button 
                className="btn btn-danger" 
                onClick={() => handleDeleteSupplier(deleteConfirm._id)}
                disabled={deleting}
              >
                {deleting ? <span className="spinner" /> : <><Trash2 size={15} /> Delete Supplier</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate Confirmation Modal */}
      {deactivateConfirm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <div>
                <h3 style={{ fontSize: '1.0625rem', fontWeight: 700, color: 'var(--warning)' }}>
                  {deactivateConfirm.isActive === false ? 'Reactivate Supplier' : 'Deactivate Supplier'}
                </h3>
                <p style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', marginTop: 2 }}>
                  {deactivateConfirm.isActive === false ? 'Restore Vendor Portal access.' : 'Temporarily disable this supplier account.'}
                </p>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setDeactivateConfirm(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-700)', marginBottom: 16, lineHeight: 1.6 }}>
                Are you sure you want to {deactivateConfirm.isActive === false ? 'reactivate' : 'deactivate'} <strong>{deactivateConfirm.name}</strong>? 
              </p>
              <div style={{
                background: 'var(--warning-light)', borderRadius: 'var(--radius-md)',
                padding: '12px 14px', fontSize: '0.8125rem', color: 'var(--warning)', lineHeight: 1.5
              }}>
                <strong>ℹ️ Info:</strong> The supplier will not be able to access the portal until reactivated. Their account data will be preserved.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeactivateConfirm(null)} disabled={deactivating}>Cancel</button>
              <button 
                className="btn btn-warning" 
                onClick={() => handleToggleSupplierActive(deactivateConfirm._id)}
                disabled={deactivating}
              >
                {deactivating ? <span className="spinner" /> : <><Power size={15} /> {deactivateConfirm.isActive === false ? 'Reactivate Supplier' : 'Deactivate Supplier'}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
